// Cascade Slot Machine type definitions

export type CascadeSlotPartType =
  | 'game_background'   // full game container background
  | 'slot_background'   // slot machine body background (behind grid)
  | 'header'
  | 'grid'
  | 'balance_text'
  | 'buy_feature'
  | 'win_text'
  | 'spin_options'      // replaces turbo_button + auto_button in bottom bar
  | 'spin_button'
  | 'bet_selector'
  | 'win_banner'        // shows between grid and info bar
  | 'symbol'
  // Config panel sub-parts
  | 'config_panel'
  | 'config_panel_bg'
  | 'config_close_btn'
  | 'turbo_toggle'
  | 'auto_toggle'
  | 'auto_count_grid'
  | 'config_confirm_btn'
  // Bet panel sub-parts
  | 'bet_panel'
  | 'bet_panel_bg'
  | 'bet_close_btn'
  | 'bet_display'
  | 'bet_minus_btn'
  | 'bet_plus_btn'
  | 'bet_progress_bar'
  | 'bet_level_grid'
  | 'bet_confirm_btn';

export type CascadeSymbolCategory = 'high' | 'normal' | 'scatter' | 'bonus' | 'wild';

// ── Spine Symbol Animation Types ──────────────────────────────────

export type SymbolAnimationTrigger =
  | 'idle' | 'prepare' | 'match' | 'drop'
  | 'spawn' | 'nearwin' | 'highlight';

/** Maps animation trigger types to Spine animation names (from the .skel file). */
export interface SymbolSpineAnimations {
  idle?: string;       // Loop on idle (wild/scatter auto-play)
  prepare?: string;    // Win validation cascade (column-by-column)
  match?: string;      // Simultaneous on all winning symbols after prepare
  drop?: string;       // Symbol landing animation (scatter)
  spawn?: string;      // Transformation animation (gold → wild)
  nearwin?: string;    // Near-win excitement loop (scatter)
  highlight?: string;  // Win presentation loop (reuses match if not set)
}

/** Spine asset reference and animation configuration for a symbol. */
export interface SymbolSpineConfig {
  skeletonPath: string;          // .skel/.json file path in project
  atlasPath: string;             // .atlas file path
  texturePaths: string[];        // .png texture file paths
  animations: SymbolSpineAnimations;
  defaultSkin?: string;          // Spine skin name (if multiple)
  timeScale?: number;            // Per-symbol override, default 1.0
}

export type CascadeFallStyle = 'bounce' | 'smooth' | 'elastic' | 'drop' | 'fade';

export interface PaytableBracket {
  minCount: number;
  maxCount: number;
  payout: number;
}

export interface CascadeSymbolConfig {
  id: string;
  name: string;
  category: CascadeSymbolCategory;
  color: string;
  textColor: string;
  weight: number;
  freeGameWeight: number;
  paytable: PaytableBracket[];
  displayMode: 'color' | 'sprite' | 'spine';
  textureAssetPath?: string;
  spineConfig?: SymbolSpineConfig;
}

export interface MultiplierPoolEntry {
  value: number;
  weight: number;
}

export interface FreeSpinsConfig {
  triggerSymbolId: string;
  triggerCount: number;
  spinsAwarded: number;
  retriggerCount: number;
  retriggerSpinsAwarded: number;
}

export interface CascadeSlotConfig {
  // Grid
  rows: number;
  columns: number;
  reelHeights?: number[]; // Per-column row counts (e.g. [4,5,6,5,4]); rows = max value
  winType?: 'count' | 'ways'; // 'count' = scatter-pays (Gates of Olympus), 'ways' = ways-pay (Mahjong Ways)
  symbolWidth: number;
  symbolHeight: number;
  symbolSpacing: number;
  // Symbols
  symbols: CascadeSymbolConfig[];
  minMatchCount: number;
  countBrackets: number[];
  // Cascade
  cascadeEnabled: boolean;
  cascadeDirection: 'vertical' | 'horizontal';
  cascadeAnimSpeed: number;
  // Multiplier
  multiplierPool: MultiplierPoolEntry[];
  // Free Spins
  freeSpins: FreeSpinsConfig;
  // Economy
  betLevels: number[];
  startingBalance: number;
  buyFeatureEnabled: boolean;
  buyFeatureCostMultiplier: number;
  maxWinMultiplier: number;
  // Spine
  spineTimeScale?: number;       // Global spine time scale multiplier (default 1.0)

  // Animation
  spinDuration: number;
  turboMultiplier: number;
  winPresentDuration: number;
  cascadeRemoveDuration: number;
  cascadeFillDuration: number;
  cascadeGravityDuration: number;
  cascadeColumnDelay: number;
  winHighlightScale: number;
  cascadeFallStyle: CascadeFallStyle;

  // Reel spin animation
  reelSpinSpeed?: number;            // px/sec during cruise, default 2500
  reelSpinStopInterval?: number;     // ms between each reel stopping, default 200
  reelSpinAccelTime?: number;        // seconds for acceleration, default 0.2
  reelSpinDecelTime?: number;        // seconds for deceleration+settle, default 0.5
  reelSpinFillerCount?: number;      // random filler symbols per reel, default 25
  reelSpinBlurMax?: number;          // max blur px during full speed, default 3

  // === Extended fields for backend games ===
  scatterPaytable?: { count: number; payout: number }[];
  superScatterPaytable?: { count: number; payout: number }[];
  superScatterRate?: number[];
  hasSuperScatter?: boolean;
  maxWin?: number;
  buyFeatureScatterCount?: number[];
}

export enum CascadeGamePhase {
  Idle = 'idle',
  Spinning = 'spinning',
  ReelSpinning = 'reel_spinning',
  Evaluating = 'evaluating',
  WinPresenting = 'win_presenting',
  CascadeRemove = 'cascade_remove',
  CascadeGravity = 'cascade_gravity',
  CascadeFill = 'cascade_fill',
  FreeSpinsIntro = 'free_spins_intro',
  FreeSpinning = 'free_spinning',
  FreeSpinsOutro = 'free_spins_outro',
}

export interface GridCell {
  symbolId: string;
  multiplierValue: number;
  isWinning: boolean;
  isRemoving: boolean;
  isNew: boolean;
  isFalling: boolean;
  fallDistance: number;
  columnIndex: number;
  animationTrigger?: SymbolAnimationTrigger | null;
}

export interface WinResult {
  symbolId: string;
  count: number;
  positions: { row: number; col: number }[];
  payout: number;
  bracketIndex: number;
}

export interface CascadeSlotPartData {
  partType: CascadeSlotPartType;
  // Layout
  offsetX?: number;
  offsetY?: number;
  customWidth?: number | null;
  customHeight?: number | null;
  // Anchor
  anchorX?: number;
  anchorY?: number;
  // Visibility
  visible?: boolean;
  alpha?: number;
  // Text parts (header, balance_text, win_text)
  fontSize?: number;
  fontColor?: string;
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textAlign?: 'left' | 'center' | 'right';
  letterSpacing?: number;
  textContent?: string;
  // Button parts (spin_button, spin_options, buy_feature, bet_selector)
  bgColor?: string;
  textColor?: string;
  buttonFontSize?: number;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  buttonLabel?: string;
  iconName?: string;
  paddingX?: number;
  paddingY?: number;
  // Texture (for header background)
  textureAssetPath?: string | null;
  textureFit?: 'fill' | 'contain' | 'cover';
  // Grid part
  symbolScale?: number;
  gridPadding?: number;
  // Symbol part
  symbolId?: string;
  symbolColor?: string;
  symbolTextColor?: string;
  symbolRow?: number;
  symbolCol?: number;
  // Layout mode
  layoutMode?: 'flow' | 'free';
}

export const DEFAULT_PART_VISUAL_PROPS: Record<CascadeSlotPartType, Partial<CascadeSlotPartData>> = {
  game_background: { bgColor: '#18181b', offsetX: 0, offsetY: 0 },
  slot_background: { bgColor: '#27272a', offsetX: 0, offsetY: 0 },
  header: { fontSize: 14, fontColor: '#ffffff', fontFamily: 'Inter', offsetX: 0, offsetY: 0 },
  grid: { symbolScale: 1, gridPadding: 2, offsetX: 0, offsetY: 0 },
  spin_button: { bgColor: '#2563eb', textColor: '#ffffff', buttonFontSize: 14, borderRadius: 8, offsetX: 0, offsetY: 0 },
  spin_options: { bgColor: '#3f3f46', textColor: '#ffffff', buttonFontSize: 12, borderRadius: 6, offsetX: 0, offsetY: 0 },
  buy_feature: { bgColor: '#2563eb', textColor: '#ffffff', buttonFontSize: 12, borderRadius: 6, offsetX: 0, offsetY: 0 },
  bet_selector: { bgColor: '#27272a', textColor: '#ffffff', buttonFontSize: 12, borderRadius: 6, offsetX: 0, offsetY: 0 },
  balance_text: { fontSize: 16, fontColor: '#ffffff', fontFamily: 'Inter', offsetX: 0, offsetY: 0 },
  win_text: { fontSize: 18, fontColor: '#fbbf24', fontFamily: 'Inter', offsetX: 0, offsetY: 0 },
  win_banner: { fontSize: 20, fontColor: '#fbbf24', fontFamily: 'Inter', bgColor: '#78350f', offsetX: 0, offsetY: 0 },
  symbol: { symbolColor: '#3B82F6', symbolTextColor: '#ffffff', offsetX: 0, offsetY: 0 },
  // Config panel sub-parts
  config_panel: { bgColor: '#18181b', offsetX: 0, offsetY: 0, visible: false },
  config_panel_bg: { bgColor: '#27272a', borderRadius: 12, offsetX: 0, offsetY: 0 },
  config_close_btn: { bgColor: '#ef4444', textColor: '#ffffff', buttonFontSize: 12, borderRadius: 6, offsetX: 0, offsetY: 0 },
  turbo_toggle: { bgColor: '#3f3f46', textColor: '#ffffff', buttonFontSize: 12, borderRadius: 6, offsetX: 0, offsetY: 0 },
  auto_toggle: { bgColor: '#3f3f46', textColor: '#ffffff', buttonFontSize: 12, borderRadius: 6, offsetX: 0, offsetY: 0 },
  auto_count_grid: { bgColor: '#27272a', borderRadius: 6, offsetX: 0, offsetY: 0 },
  config_confirm_btn: { bgColor: '#2563eb', textColor: '#ffffff', buttonFontSize: 14, borderRadius: 8, offsetX: 0, offsetY: 0 },
  // Bet panel sub-parts
  bet_panel: { bgColor: '#18181b', offsetX: 0, offsetY: 0, visible: false },
  bet_panel_bg: { bgColor: '#27272a', borderRadius: 12, offsetX: 0, offsetY: 0 },
  bet_close_btn: { bgColor: '#ef4444', textColor: '#ffffff', buttonFontSize: 12, borderRadius: 6, offsetX: 0, offsetY: 0 },
  bet_display: { fontSize: 16, fontColor: '#ffffff', fontFamily: 'Inter', offsetX: 0, offsetY: 0 },
  bet_minus_btn: { bgColor: '#3f3f46', textColor: '#ffffff', buttonFontSize: 14, borderRadius: 6, offsetX: 0, offsetY: 0 },
  bet_plus_btn: { bgColor: '#3f3f46', textColor: '#ffffff', buttonFontSize: 14, borderRadius: 6, offsetX: 0, offsetY: 0 },
  bet_progress_bar: { bgColor: '#2563eb', offsetX: 0, offsetY: 0 },
  bet_level_grid: { bgColor: '#27272a', borderRadius: 6, offsetX: 0, offsetY: 0 },
  bet_confirm_btn: { bgColor: '#2563eb', textColor: '#ffffff', buttonFontSize: 14, borderRadius: 8, offsetX: 0, offsetY: 0 },
};

export function resolvePartData(data: Record<string, unknown>): CascadeSlotPartData {
  const partType = (data.partType as CascadeSlotPartType) || 'header';
  const defaults = DEFAULT_PART_VISUAL_PROPS[partType] || {};
  return {
    partType,
    offsetX: (data.offsetX as number) ?? defaults.offsetX ?? 0,
    offsetY: (data.offsetY as number) ?? defaults.offsetY ?? 0,
    customWidth: (data.customWidth as number | null) ?? defaults.customWidth ?? null,
    customHeight: (data.customHeight as number | null) ?? defaults.customHeight ?? null,
    anchorX: (data.anchorX as number) ?? defaults.anchorX,
    anchorY: (data.anchorY as number) ?? defaults.anchorY,
    visible: (data.visible as boolean) ?? defaults.visible ?? true,
    alpha: (data.alpha as number) ?? defaults.alpha ?? 1,
    fontSize: (data.fontSize as number) ?? defaults.fontSize,
    fontColor: (data.fontColor as string) ?? defaults.fontColor,
    fontFamily: (data.fontFamily as string) ?? defaults.fontFamily,
    fontWeight: (data.fontWeight as 'normal' | 'bold') ?? defaults.fontWeight,
    fontStyle: (data.fontStyle as 'normal' | 'italic') ?? defaults.fontStyle,
    textAlign: (data.textAlign as 'left' | 'center' | 'right') ?? defaults.textAlign,
    letterSpacing: (data.letterSpacing as number) ?? defaults.letterSpacing,
    textContent: (data.textContent as string) ?? defaults.textContent,
    bgColor: (data.bgColor as string) ?? defaults.bgColor,
    textColor: (data.textColor as string) ?? defaults.textColor,
    buttonFontSize: (data.buttonFontSize as number) ?? defaults.buttonFontSize,
    borderRadius: (data.borderRadius as number) ?? defaults.borderRadius,
    borderWidth: (data.borderWidth as number) ?? defaults.borderWidth,
    borderColor: (data.borderColor as string) ?? defaults.borderColor,
    buttonLabel: (data.buttonLabel as string) ?? defaults.buttonLabel,
    iconName: (data.iconName as string) ?? defaults.iconName,
    paddingX: (data.paddingX as number) ?? defaults.paddingX,
    paddingY: (data.paddingY as number) ?? defaults.paddingY,
    textureAssetPath: (data.textureAssetPath as string | null) ?? defaults.textureAssetPath,
    textureFit: (data.textureFit as 'fill' | 'contain' | 'cover') ?? defaults.textureFit,
    symbolScale: (data.symbolScale as number) ?? defaults.symbolScale,
    gridPadding: (data.gridPadding as number) ?? defaults.gridPadding,
    symbolId: (data.symbolId as string) ?? defaults.symbolId,
    symbolColor: (data.symbolColor as string) ?? defaults.symbolColor,
    symbolTextColor: (data.symbolTextColor as string) ?? defaults.symbolTextColor,
    symbolRow: (data.symbolRow as number) ?? defaults.symbolRow,
    symbolCol: (data.symbolCol as number) ?? defaults.symbolCol,
    layoutMode: (data.layoutMode as 'flow' | 'free') ?? defaults.layoutMode ?? 'flow',
  };
}

export const DEFAULT_CASCADE_SLOT_CONFIG: CascadeSlotConfig = {
  rows: 6,
  columns: 5,
  symbolWidth: 120,
  symbolHeight: 100,
  symbolSpacing: 4,
  symbols: [
    // H1-H4 (high-pay)
    { id: 'H1', name: 'Crown', category: 'high', color: '#DC2626', textColor: '#FFFFFF', weight: 40, freeGameWeight: 95, paytable: [{ minCount: 8, maxCount: 9, payout: 200 }, { minCount: 10, maxCount: 11, payout: 500 }, { minCount: 12, maxCount: 30, payout: 1000 }], displayMode: 'color' },
    { id: 'H2', name: 'Hourglass', category: 'high', color: '#7C3AED', textColor: '#FFFFFF', weight: 40, freeGameWeight: 100, paytable: [{ minCount: 8, maxCount: 9, payout: 50 }, { minCount: 10, maxCount: 11, payout: 200 }, { minCount: 12, maxCount: 30, payout: 500 }], displayMode: 'color' },
    { id: 'H3', name: 'Ring', category: 'high', color: '#2563EB', textColor: '#FFFFFF', weight: 40, freeGameWeight: 100, paytable: [{ minCount: 8, maxCount: 9, payout: 40 }, { minCount: 10, maxCount: 11, payout: 100 }, { minCount: 12, maxCount: 30, payout: 300 }], displayMode: 'color' },
    { id: 'H4', name: 'Chalice', category: 'high', color: '#16A34A', textColor: '#FFFFFF', weight: 40, freeGameWeight: 100, paytable: [{ minCount: 8, maxCount: 9, payout: 30 }, { minCount: 10, maxCount: 11, payout: 40 }, { minCount: 12, maxCount: 30, payout: 240 }], displayMode: 'color' },
    // N1-N5 (normal)
    { id: 'N1', name: 'Blue Gem', category: 'normal', color: '#3B82F6', textColor: '#FFFFFF', weight: 100, freeGameWeight: 100, paytable: [{ minCount: 8, maxCount: 9, payout: 20 }, { minCount: 10, maxCount: 11, payout: 30 }, { minCount: 12, maxCount: 30, payout: 200 }], displayMode: 'color' },
    { id: 'N2', name: 'Green Gem', category: 'normal', color: '#22C55E', textColor: '#FFFFFF', weight: 100, freeGameWeight: 45, paytable: [{ minCount: 8, maxCount: 9, payout: 16 }, { minCount: 10, maxCount: 11, payout: 24 }, { minCount: 12, maxCount: 30, payout: 160 }], displayMode: 'color' },
    { id: 'N3', name: 'Red Gem', category: 'normal', color: '#EF4444', textColor: '#FFFFFF', weight: 100, freeGameWeight: 40, paytable: [{ minCount: 8, maxCount: 9, payout: 10 }, { minCount: 10, maxCount: 11, payout: 20 }, { minCount: 12, maxCount: 30, payout: 100 }], displayMode: 'color' },
    { id: 'N4', name: 'Yellow Gem', category: 'normal', color: '#EAB308', textColor: '#000000', weight: 100, freeGameWeight: 40, paytable: [{ minCount: 8, maxCount: 9, payout: 8 }, { minCount: 10, maxCount: 11, payout: 18 }, { minCount: 12, maxCount: 30, payout: 80 }], displayMode: 'color' },
    { id: 'N5', name: 'Purple Gem', category: 'normal', color: '#A855F7', textColor: '#FFFFFF', weight: 100, freeGameWeight: 40, paytable: [{ minCount: 8, maxCount: 9, payout: 5 }, { minCount: 10, maxCount: 11, payout: 15 }, { minCount: 12, maxCount: 30, payout: 40 }], displayMode: 'color' },
    // SC (scatter)
    { id: 'SC', name: 'Scatter', category: 'scatter', color: '#F59E0B', textColor: '#000000', weight: 16, freeGameWeight: 12, paytable: [], displayMode: 'color' },
    // BN (bonus/multiplier)
    { id: 'BN', name: 'Multiplier', category: 'bonus', color: '#EC4899', textColor: '#FFFFFF', weight: 5, freeGameWeight: 10, paytable: [], displayMode: 'color' },
  ],
  minMatchCount: 8,
  countBrackets: [8, 10, 12],
  cascadeEnabled: true,
  cascadeDirection: 'horizontal',
  cascadeAnimSpeed: 300,
  multiplierPool: [
    { value: 2, weight: 285000 },
    { value: 3, weight: 149012 },
    { value: 4, weight: 59605 },
    { value: 5, weight: 23842 },
    { value: 6, weight: 9537 },
    { value: 8, weight: 3815 },
    { value: 10, weight: 1526 },
    { value: 12, weight: 610 },
    { value: 15, weight: 244 },
    { value: 20, weight: 98 },
    { value: 25, weight: 39 },
    { value: 50, weight: 16 },
    { value: 100, weight: 6 },
    { value: 250, weight: 3 },
    { value: 500, weight: 1 },
  ],
  freeSpins: {
    triggerSymbolId: 'SC',
    triggerCount: 4,
    spinsAwarded: 15,
    retriggerCount: 3,
    retriggerSpinsAwarded: 5,
  },
  betLevels: [20, 30, 2000, 10000],
  startingBalance: 10000,
  buyFeatureEnabled: true,
  buyFeatureCostMultiplier: 100,
  maxWinMultiplier: 50000,
  spinDuration: 0.5,
  turboMultiplier: 3,
  winPresentDuration: 0.8,
  cascadeRemoveDuration: 0.3,
  cascadeFillDuration: 0.5,
  cascadeGravityDuration: 0.25,
  cascadeColumnDelay: 30,
  winHighlightScale: 1.1,
  cascadeFallStyle: 'bounce',
};
